/**
 * DateTimePickerDropdown
 * Combined date and time picker with calendar and analog clock interface
 */

import React, { useState, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const DateTimePickerDropdown = ({
  value,
  onChange,
  minDate = new Date(),
  disabled = false,
  placeholder = 'Select date & time'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(value);
  const [clockMode, setClockMode] = useState('hour'); // 'hour' | 'minute'
  const [selectedHour, setSelectedHour] = useState(value ? (value.getHours() % 12 || 12) : 10);
  const [selectedMinute, setSelectedMinute] = useState(value ? Math.floor(value.getMinutes() / 5) * 5 : 0);
  const [selectedPeriod, setSelectedPeriod] = useState(value ? (value.getHours() >= 12 ? 'PM' : 'AM') : 'AM');

  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Update internal state when value changes externally
  useEffect(() => {
    if (value) {
      setSelectedDate(value);
      setSelectedHour(value.getHours() % 12 || 12);
      setSelectedMinute(Math.floor(value.getMinutes() / 5) * 5);
      setSelectedPeriod(value.getHours() >= 12 ? 'PM' : 'AM');
    }
  }, [value]);

  // Build final datetime and call onChange
  const applyDateTime = (date, hour, minute, period) => {
    if (!date) return;

    const newDate = new Date(date);
    let hours = hour;
    if (period === 'PM' && hour !== 12) hours = hour + 12;
    if (period === 'AM' && hour === 12) hours = 0;

    newDate.setHours(hours, minute, 0, 0);
    onChange(newDate);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    applyDateTime(date, selectedHour, selectedMinute, selectedPeriod);
  };

  const handleHourClick = (hour) => {
    setSelectedHour(hour);
    setClockMode('minute');
    applyDateTime(selectedDate, hour, selectedMinute, selectedPeriod);
  };

  const handleMinuteClick = (minute) => {
    setSelectedMinute(minute);
    applyDateTime(selectedDate, selectedHour, minute, selectedPeriod);
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    applyDateTime(selectedDate, selectedHour, selectedMinute, period);
  };

  // Format display value
  const formatDisplayValue = () => {
    if (!value) return '';
    const options = {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    return value.toLocaleString('en-US', options);
  };

  // Render analog clock
  const renderClock = () => {
    const radius = 90;
    const centerX = 100;
    const centerY = 100;

    if (clockMode === 'hour') {
      // Hour selection
      const hours = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
      return (
        <div className="flex flex-col items-center">
          <p className="text-xs text-gray-500 mb-2">Select Hour</p>
          <svg width="200" height="200" viewBox="0 0 200 200">
            {/* Clock face */}
            <circle cx={centerX} cy={centerY} r={radius} fill="#f8fafc" stroke="#e2e8f0" strokeWidth="2" />

            {/* Hour markers */}
            {hours.map((hour, index) => {
              const angle = (index * 30 - 90) * (Math.PI / 180);
              const x = centerX + (radius - 25) * Math.cos(angle);
              const y = centerY + (radius - 25) * Math.sin(angle);
              const isSelected = selectedHour === hour;

              return (
                <g key={hour} onClick={() => handleHourClick(hour)} style={{ cursor: 'pointer' }}>
                  <circle
                    cx={x}
                    cy={y}
                    r="18"
                    fill={isSelected ? '#0d9488' : 'transparent'}
                    className="hover:fill-teal-100 transition-colors"
                  />
                  <text
                    x={x}
                    y={y}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize="14"
                    fontWeight={isSelected ? '600' : '400'}
                    fill={isSelected ? 'white' : '#374151'}
                  >
                    {hour}
                  </text>
                </g>
              );
            })}

            {/* Center dot */}
            <circle cx={centerX} cy={centerY} r="4" fill="#0d9488" />

            {/* Hour hand */}
            {selectedHour && (() => {
              const hourIndex = selectedHour === 12 ? 0 : selectedHour;
              const angle = (hourIndex * 30 - 90) * (Math.PI / 180);
              const handLength = radius - 45;
              const x2 = centerX + handLength * Math.cos(angle);
              const y2 = centerY + handLength * Math.sin(angle);
              return (
                <line x1={centerX} y1={centerY} x2={x2} y2={y2} stroke="#0d9488" strokeWidth="2" />
              );
            })()}
          </svg>
        </div>
      );
    }

    // Minute selection
    const minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
    return (
      <div className="flex flex-col items-center">
        <p className="text-xs text-gray-500 mb-2">Select Minute</p>
        <svg width="200" height="200" viewBox="0 0 200 200">
          {/* Clock face */}
          <circle cx={centerX} cy={centerY} r={radius} fill="#f8fafc" stroke="#e2e8f0" strokeWidth="2" />

          {/* Minute markers */}
          {minutes.map((minute, index) => {
            const angle = (index * 30 - 90) * (Math.PI / 180);
            const x = centerX + (radius - 25) * Math.cos(angle);
            const y = centerY + (radius - 25) * Math.sin(angle);
            const isSelected = selectedMinute === minute;

            return (
              <g key={minute} onClick={() => handleMinuteClick(minute)} style={{ cursor: 'pointer' }}>
                <circle
                  cx={x}
                  cy={y}
                  r="18"
                  fill={isSelected ? '#0d9488' : 'transparent'}
                  className="hover:fill-teal-100 transition-colors"
                />
                <text
                  x={x}
                  y={y}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize="14"
                  fontWeight={isSelected ? '600' : '400'}
                  fill={isSelected ? 'white' : '#374151'}
                >
                  {minute.toString().padStart(2, '0')}
                </text>
              </g>
            );
          })}

          {/* Center dot */}
          <circle cx={centerX} cy={centerY} r="4" fill="#0d9488" />

          {/* Minute hand */}
          {(() => {
            const minuteIndex = selectedMinute / 5;
            const angle = (minuteIndex * 30 - 90) * (Math.PI / 180);
            const handLength = radius - 35;
            const x2 = centerX + handLength * Math.cos(angle);
            const y2 = centerY + handLength * Math.sin(angle);
            return (
              <line x1={centerX} y1={centerY} x2={x2} y2={y2} stroke="#0d9488" strokeWidth="2" />
            );
          })()}
        </svg>
      </div>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-lg text-left flex items-center justify-between
          ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-50' : 'bg-white hover:border-gray-400 cursor-pointer'}
          ${isOpen ? 'border-teal-500 ring-2 ring-teal-500' : 'border-gray-300'}
        `}
      >
        <span className={value ? 'text-gray-900' : 'text-gray-400'}>
          {value ? formatDisplayValue() : placeholder}
        </span>
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-4"
          style={{ left: '50%', transform: 'translateX(-50%)' }}
        >
          <div className="flex gap-4">
            {/* Calendar */}
            <div className="border-r border-gray-200 pr-4">
              <DatePicker
                selected={selectedDate}
                onChange={handleDateChange}
                minDate={minDate}
                inline
                calendarClassName="!border-0"
              />
            </div>

            {/* Time Picker */}
            <div className="flex flex-col items-center min-w-[220px]">
              {/* Time Display */}
              <div className="flex items-center gap-1 mb-3 text-2xl font-semibold text-gray-800">
                <button
                  type="button"
                  onClick={() => setClockMode('hour')}
                  className={`px-2 py-1 rounded ${clockMode === 'hour' ? 'bg-teal-100 text-teal-700' : 'hover:bg-gray-100'}`}
                >
                  {selectedHour.toString().padStart(2, '0')}
                </button>
                <span>:</span>
                <button
                  type="button"
                  onClick={() => setClockMode('minute')}
                  className={`px-2 py-1 rounded ${clockMode === 'minute' ? 'bg-teal-100 text-teal-700' : 'hover:bg-gray-100'}`}
                >
                  {selectedMinute.toString().padStart(2, '0')}
                </button>
              </div>

              {/* Analog Clock */}
              {renderClock()}

              {/* AM/PM Toggle */}
              <div className="flex gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => handlePeriodChange('AM')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors
                    ${selectedPeriod === 'AM'
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  AM
                </button>
                <button
                  type="button"
                  onClick={() => handlePeriodChange('PM')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors
                    ${selectedPeriod === 'PM'
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  PM
                </button>
              </div>
            </div>
          </div>

          {/* Done Button */}
          <div className="flex justify-end mt-4 pt-3 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateTimePickerDropdown;
